import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Toolbar } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar } from "./utils/styled";
import AppHeaderMenu from "./menu/AppHeaderMenu";
import { LOCAL_STORAGE_BADGE_PRINT } from "common/constants";
import { PAPER_SIZE } from "common/enums";
import { Routes } from "routes";
import { AppHeaderOptions } from "./options/AppHeaderOptions";
import { RootState } from "redux/store";
import { EventEditionDetails } from "common/types";
import { LogoContainer } from "./logoContainer/LogoContainer";
import { setBadgePrintFormat } from "../../redux/metadata/slice/metadataSlice";
import { cmsEventDetailsSelector } from "../../redux/eventEdition/selectors/eventEdition.selectors";

type Props = {
  hideOptions?: boolean;
};

export const AppHeader: React.FC<Props> = ({ hideOptions }) => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const cmsEventDetails = useSelector(cmsEventDetailsSelector);

  const reduxState = useSelector((state: RootState) => state);
  const eventEdition = reduxState.eventEdition?.eventEdition as EventEditionDetails;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [logoUrl, setLogoUrl] = React.useState<string>("");

  const isMenuOpen = Boolean(anchorEl);
  const menuId = "primary-search-account-menu";

  const handleProfileMenuOpen = ({ currentTarget }: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const initializeDefaultFormat = () => {
    const savedFormat = localStorage.getItem(LOCAL_STORAGE_BADGE_PRINT) as PAPER_SIZE | undefined;
    if (savedFormat && Object.values(PAPER_SIZE).includes(savedFormat)) {
      dispatch(setBadgePrintFormat(savedFormat));
    }
  };

  const getLogo = useCallback(async () => {
    try {
      const domain = new URL(eventEdition.domain || "").origin;
      setLogoUrl(`${domain}${cmsEventDetails?.siteDetails?.logo?.url}`);
    } catch(e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    initializeDefaultFormat();
    getLogo();
  }, [cmsEventDetails]);

  const onRxLogoClick = () => {
    !hideOptions && history(Routes.homepage);
  };

  const getAppHeaderOptions = () => {
    return {
      hideGlobalSettings: hideOptions,
      hidePrintSettings: hideOptions,
      hideLogout: hideOptions
    };
  };

  return (
    <>
      <AppBar data-testid="app-header-bar">
        <Toolbar>
          <LogoContainer logoUrl={logoUrl} isFreya={hideOptions} onRxLogoClick={onRxLogoClick} />
          <Box sx={{ flexGrow: 1 }} />
          {hideOptions && (
            <IconButton data-testid="profile-menu-button" sx={{ mr: 1 }} onClick={handleProfileMenuOpen}>
              <MenuIcon color="primary" aria-controls={menuId} aria-haspopup="true" />
            </IconButton>
          )}
          {!hideOptions && <AppHeaderOptions menuId={menuId} handleProfileMenuOpen={handleProfileMenuOpen} />}
        </Toolbar>
      </AppBar>
      <AppHeaderMenu
        options={getAppHeaderOptions()}
        menuId={menuId}
        isMenuOpen={isMenuOpen}
        handleMenuClose={handleMenuClose}
        anchorEl={anchorEl}
      />
    </>
  );
};
